
import { useState, useEffect } from "react";
import { extractOriginalFileName } from "@/utils/fileOperations";
import type { FileObject } from "@supabase/storage-js";

interface UseFileFilteringProps {
  files: FileObject[];
}

export const useFileFiltering = ({ files }: UseFileFilteringProps) => {
  const [filteredFiles, setFilteredFiles] = useState<FileObject[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<"name" | "date" | "size">("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  useEffect(() => {
    let filtered = files.filter(file =>
      file.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      extractOriginalFileName(file.name).toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Sort files
    filtered.sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (sortBy) {
        case "name":
          aValue = extractOriginalFileName(a.name).toLowerCase();
          bValue = extractOriginalFileName(b.name).toLowerCase();
          break;
        case "date":
          aValue = new Date(a.updated_at || a.created_at).getTime();
          bValue = new Date(b.updated_at || b.created_at).getTime();
          break;
        case "size":
          aValue = a.metadata?.size || 0;
          bValue = b.metadata?.size || 0;
          break;
        default:
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
      }

      if (sortOrder === "asc") {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    setFilteredFiles(filtered);
  }, [files, searchQuery, sortBy, sortOrder]);

  return {
    filteredFiles,
    searchQuery,
    setSearchQuery,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
  };
};
